import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Smartphone,
  Shield,
  MessageCircle
} from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { checkRateLimit, getDeviceFingerprint } from '../utils/rateLimiter';
import { validateFormSubmission, sanitizeInput } from '../utils/spamDetection';

export default function ContactPage() {
  const auth = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    subject: '',
    priority: 'medium',
    preferredResponse: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    
    // Scroll to first error field if validation fails
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Rate limiting check
    const userId = auth.user?.uid || getDeviceFingerprint();
    const rateLimitCheck = checkRateLimit(userId, 'CONTACT_FORM');
    if (!rateLimitCheck.allowed) {
      setErrors({ submit: rateLimitCheck.message || 'Too many submissions. Please wait.' });
      return;
    }
    
    // Spam detection
    const spamCheck = validateFormSubmission({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      subject: formData.subject
    });
    
    if (!spamCheck.isValid) {
      setErrors({ submit: spamCheck.errors.join('. ') });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // Save to Firebase first
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service || 'Not specified',
        subject: formData.subject,
        message: formData.message,
        priority: formData.priority,
        preferredResponse: formData.preferredResponse,
        status: 'pending', // pending, contacted
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'contacts'), contactData);

      // Send email using Web3Forms API
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || '94f15d34-72ee-4a6f-9045-d1d1199f39c0',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: `RAPSAPP Contact: ${formData.subject}`,
          message: `
Service Interested: ${formData.service || 'Not specified'}
Preferred Response: ${formData.preferredResponse}
Priority: ${formData.priority}

Message:
${formData.message}
          `,
          from_name: 'RAPSAPP Contact Form',
          replyto: formData.email,
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('Form submitted successfully:', formData);
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          email: '', 
          phone: '', 
          service: '', 
          message: '', 
          subject: '', 
          priority: 'medium',
          preferredResponse: 'email'
        });
        setErrors({});
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white py-12 md:py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 md:top-20 left-10 md:left-20 w-16 md:w-32 h-16 md:h-32 bg-white opacity-10 rounded-full"></div>
          <div className="absolute bottom-10 md:bottom-20 right-10 md:right-20 w-24 md:w-48 h-24 md:h-48 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-20 md:top-40 right-20 md:right-40 w-10 md:w-20 h-10 md:h-20 bg-white opacity-15 rounded-full"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 md:mb-6 lg:mb-8 bg-gradient-to-r from-white to-primary-100 bg-clip-text text-transparent"
            >
              Get In Touch
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-sm md:text-lg lg:text-xl xl:text-2xl text-primary-100 max-w-4xl mx-auto leading-relaxed px-4"
            >
              Have questions or need assistance? We're here to help you 24/7. 
              <br className="hidden md:block" />
              Connect with our expert team and get the support you deserve.
            </motion.p>
          </div>

          {/* Quick Contact Options */}
          <motion.div 
            className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8 px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="flex items-center justify-center space-x-2 bg-white bg-opacity-20 px-4 md:px-6 py-2.5 md:py-3 rounded-full backdrop-blur-sm">
              <Phone className="h-4 w-4 md:h-5 md:w-5 text-green-400" />
              <span className="text-white font-medium text-xs md:text-sm lg:text-base">Call: +91 7018401540</span>
            </div>
            <div className="flex items-center justify-center space-x-2 bg-white bg-opacity-20 px-4 md:px-6 py-2.5 md:py-3 rounded-full backdrop-blur-sm">
              <Mail className="h-4 w-4 md:h-5 md:w-5 text-blue-400" />
              <span className="text-white font-medium text-xs md:text-sm lg:text-base">Email: rapsappservice@gmail.com</span>
            </div>
          </motion.div>

          {/* Response Time Stats */}
          <motion.div 
            className="flex flex-wrap justify-center gap-4 md:gap-6 lg:gap-8 text-center px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-1">&lt; 2 mins</div>
              <div className="text-primary-200 text-xs md:text-sm">Average Response Time</div>
            </div>
            <div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-1">24/7</div>
              <div className="text-primary-200 text-xs md:text-sm">Customer Support</div>
            </div>
            <div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-1">99.9%</div>
              <div className="text-primary-200 text-xs md:text-sm">Resolution Rate</div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Form - Shows first on mobile */}
            <motion.div 
              className="lg:col-span-2 order-1 lg:order-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Send us a Message</h2>
                  <p className="text-gray-600 text-sm md:text-base lg:text-lg">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <div>
                      <h3 className="text-green-800 font-medium">Message sent successfully!</h3>
                      <p className="text-green-700 text-sm">We'll get back to you within 2 hours.</p>
                    </div>
                  </motion.div>
                )}
                
                {submitStatus === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
                  >
                    <AlertCircle className="h-6 w-6 text-red-600" />
                    <div>
                      <h3 className="text-red-800 font-medium">Failed to send message</h3>
                      <p className="text-red-700 text-sm">Please try again or contact us directly.</p>
                    </div>
                  </motion.div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300 ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && (
                        <motion.p 
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600 flex items-center gap-1"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.name}
                        </motion.p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300 ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <motion.p 
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600 flex items-center gap-1"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.email}
                        </motion.p>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300 ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && (
                        <motion.p 
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600 flex items-center gap-1"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.phone}
                        </motion.p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Level
                      </label>
                      <select
                        id="priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Category
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300"
                      >
                        <option value="">Select a service</option>
                        <option value="cleaning">🏠 Home Cleaning</option>
                        <option value="plumbing">🔧 Plumbing Services</option>
                        <option value="electrical">⚡ Electrical Work</option>
                        <option value="appliance">📱 Appliance Repair</option>
                        <option value="pest-control">🐛 Pest Control</option>
                        <option value="painting">🎨 Painting Services</option>
                        <option value="carpentry">🔨 Carpentry Work</option>
                        <option value="beauty">💄 Beauty & Salon</option>
                        <option value="travel">✈️ Travel Packages</option>
                        <option value="general">💬 General Inquiry</option>
                        <option value="complaint">⚠️ Complaint/Issue</option>
                        <option value="other">🔹 Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300 ${
                          errors.subject ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Brief subject of your message"
                      />
                      {errors.subject && (
                        <motion.p 
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600 flex items-center gap-1"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.subject}
                        </motion.p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Preferred Response Method *
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredResponse"
                          value="email"
                          checked={formData.preferredResponse === 'email'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <Mail className="h-5 w-5 text-primary-600" />
                        <span className="text-sm text-gray-700">Email</span>
                      </label>
                      
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredResponse"
                          value="phone"
                          checked={formData.preferredResponse === 'phone'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <Phone className="h-5 w-5 text-primary-600" />
                        <span className="text-sm text-gray-700">Phone Call</span>
                      </label>
                      
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredResponse"
                          value="sms"
                          checked={formData.preferredResponse === 'sms'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <Mail className="h-5 w-5 text-primary-600" />
                        <span className="text-sm text-gray-700">SMS</span>
                      </label>
                      
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredResponse"
                          value="whatsapp"
                          checked={formData.preferredResponse === 'whatsapp'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <Smartphone className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-gray-700">WhatsApp</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 hover:border-primary-300 resize-none ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Tell us how we can help you..."
                    />
                    {errors.message && (
                      <motion.p 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center gap-1"
                      >
                        <AlertCircle className="h-4 w-4" />
                        {errors.message}
                      </motion.p>
                    )}
                  </div>
                  
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300 flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    <Send className="h-5 w-5" />
                    <span>{isSubmitting ? 'Sending Message...' : 'Send Message'}</span>
                  </motion.button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information - Shows second on mobile */}
            <motion.div 
              className="lg:col-span-1 order-2 lg:order-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 lg:p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">Contact Information</h2>
                
                <div className="space-y-4 md:space-y-6">
                  <motion.div 
                    className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 rounded-xl hover:bg-primary-50 transition-colors group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <MapPin className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Visit Our Office</h3>
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        Joginder Nagar, Mandi,<br />
                        Himachal Pradesh
                      </p>
                      <button className="text-primary-600 text-xs md:text-sm font-medium hover:underline mt-1 md:mt-2 flex items-center">
                        Get Directions <ExternalLink className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 rounded-xl hover:bg-green-50 transition-colors group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <Phone className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Call Us Anytime</h3>
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        <a href="tel:+917018401540" className="hover:text-green-600 transition-colors">+91 7018401540</a>
                      </p>
                      <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full mt-1 md:mt-2">
                        Available 24/7
                      </span>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 rounded-xl hover:bg-emerald-50 transition-colors group cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    onClick={() => {
                      const message = encodeURIComponent("Hello RAPSAPP! I would like to inquire about your services.");
                      window.open(`https://wa.me/917018401540?text=${message}`, '_blank');
                    }}
                  >
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <MessageCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">WhatsApp Support</h3>
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        <span className="hover:text-emerald-600 transition-colors">+91 7018401540</span>
                      </p>
                      <span className="inline-block bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full mt-1 md:mt-2">
                        Instant Chat Support
                      </span>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 rounded-xl hover:bg-blue-50 transition-colors group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <Mail className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Email Support</h3>
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        <a href="mailto:rapsappservice@gmail.com" className="hover:text-blue-600 transition-colors break-all">rapsappservice@gmail.com</a><br />
                        <span className="text-xs md:text-sm text-gray-500">www.rapsapp.in</span>
                      </p>
                      <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full mt-1 md:mt-2">
                        Response within 2 hours
                      </span>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 rounded-xl hover:bg-purple-50 transition-colors group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <Clock className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Business Hours</h3>
                      <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                        Mon - Sat: 8:00 AM - 10:00 PM<br />
                        Sunday: 9:00 AM - 8:00 PM
                      </p>
                      <span className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full mt-1 md:mt-2">
                        Extended Weekend Hours
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="py-8 md:py-12 lg:py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-8 md:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
              Why Choose RapsApp Services?
            </h2>
            <p className="text-sm md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Experience excellence in every service. We bring professionalism, reliability, and quality to your doorstep.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {/* Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl md:rounded-2xl p-4 md:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-primary-100 rounded-lg md:rounded-xl p-2 md:p-3 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-3 md:mb-4">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Trusted Professionals</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Our service providers undergo thorough background checks and professional training to ensure top-quality service.
              </p>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl md:rounded-2xl p-4 md:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-green-100 rounded-lg md:rounded-xl p-2 md:p-3 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-3 md:mb-4">
                <Clock className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">24/7 Availability</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Round-the-clock service to handle your requirements, including emergency services when you need them most.
              </p>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl md:rounded-2xl p-4 md:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-blue-100 rounded-lg md:rounded-xl p-2 md:p-3 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-3 md:mb-4">
                <Shield className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Instant Support</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Quick response times and real-time support through multiple channels including call and email.
              </p>
            </motion.div>

            {/* Card 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-xl md:rounded-2xl p-4 md:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-purple-100 rounded-lg md:rounded-xl p-2 md:p-3 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-3 md:mb-4">
                <Send className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Satisfaction Guaranteed</h3>
              <p className="text-gray-600 text-sm md:text-base">
                We stand behind our work with a 100% satisfaction guarantee. Your happiness is our priority.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
